import { useState } from 'react';
import { Alert, Button, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import type { WebhookEndpoint } from '../../app/types';
import PageSection from '../shared/PageSection';

interface WebhooksPageProps {
  readonly tenantId: string;
  readonly webhooks: WebhookEndpoint[];
  readonly onCreateWebhook: (payload: { tenantId: string; url: string; enabled?: boolean; signingSecret?: string }) => Promise<void>;
  readonly onTestWebhook: (webhookId: string) => Promise<void>;
}

export default function WebhooksPage({ tenantId, webhooks, onCreateWebhook, onTestWebhook }: WebhooksPageProps) {
  const [url, setUrl] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [error, setError] = useState('');

  const create = async () => {
    try {
      setError('');
      await onCreateWebhook({
        tenantId,
        url,
        enabled: true,
        signingSecret: signingSecret || undefined,
      });
      setUrl('');
      setSigningSecret('');
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Unable to create webhook.');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={7}>
        <PageSection title="Webhook Destinations" subtitle="Outbound alert delivery targets for the active tenant.">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Secret</TableCell>
                <TableCell>Test</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map((webhook) => (
                <TableRow key={webhook.webhookId}>
                  <TableCell>{webhook.url}</TableCell>
                  <TableCell>{webhook.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                  <TableCell>{webhook.signingSecret ? 'Configured' : 'Generated server-side'}</TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => void onTestWebhook(webhook.webhookId)}>
                      Queue Test
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PageSection>
      </Grid>
      <Grid item xs={12} lg={5}>
        <PageSection title="Create Webhook">
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField label="Destination URL" value={url} onChange={(event) => setUrl(event.target.value)} />
            <TextField
              label="Signing Secret (Optional)"
              value={signingSecret}
              onChange={(event) => setSigningSecret(event.target.value)}
            />
            <Button variant="contained" onClick={() => void create()} disabled={!url}>
              Create Webhook
            </Button>
          </Stack>
        </PageSection>
      </Grid>
    </Grid>
  );
}
